import React, { useEffect, useState } from 'react';
import { socketService, SocketEvents } from '@/services/socketService';
import { toast } from 'sonner';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import { useNavigate } from 'react-router-dom';
import AuthAPI from '@/services/api-calls/auth.api';
import type { IUser } from '@/lib/types';
import Cookies from '@/lib/useCookies';

export const NotificationProvider = ({ children }: { children: React.ReactNode }) => {
    const [user, setUser] = useState<IUser | null>(null);
    const { subscribe, permission } = usePushNotifications();
    const navigate = useNavigate();

    // Fetch User Profile
    useEffect(() => {
        const fetchUser = async () => {
            try {
                const token = await Cookies.getCookie('accessToken');
                if (token) {
                    const response = await AuthAPI.getCurrentUser();
                    if (response.success && response.user) {
                        setUser(response.user as unknown as IUser);
                    }
                }
            } catch (error) {
                console.error("Failed to fetch user for notification provider:", error);
            }
        };

        fetchUser();
    }, []);

    // Initialize Socket Connection
    useEffect(() => {
        const initSocket = async () => {
            if (user?.id) {
                await socketService.init(user);
            }
        };

        initSocket();

        return () => {
            socketService.disconnect();
        };
    }, [user]);

    // Handle Push Subscription
    useEffect(() => {
        if (user?.id && permission === 'default') {
            // Optionally trigger subscribe automatically or show UI
            subscribe();
        }
    }, [user, permission, subscribe]);

    // Socket Event Listeners
    useEffect(() => {
        if (!user?.id) return;

        // 1. General Notifications
        const unsubscribeNotification = socketService.on(SocketEvents.NOTIFICATION, (data: any) => {
            toast(data.title || 'New Notification', {
                description: data.message || data.body,
                action: data.link ? {
                    label: 'View',
                    onClick: () => navigate(data.link)
                } : undefined,
            });
        });

        // 2. Email Received
        const unsubscribeEmail = socketService.on(SocketEvents.EMAIL_RECEIVED, (data: any) => {
            toast.info(`New email from ${data.from?.name || data.from?.email || 'Unknown Sender'}`, {
                description: data.subject,
                action: {
                    label: 'Open',
                    onClick: () => navigate(`/email/${data.id}`)
                }
            });
        });

        // 3. Chat Messages (Background notification from chat app)
        const unsubscribeMessage = socketService.on(SocketEvents.MESSAGE_NOTIFICATION, (data: any) => {
            // Only show toast if we aren't viewing the channel directly
            if (!data.isViewing) {
                toast(`New message from ${data.sender?.displayName || data.sender?.firstName || 'Someone'}`, {
                    description: data.content,
                    action: {
                        label: 'View',
                        onClick: () => navigate(`/apps/chat`)
                    },
                });
            }
        });

        // 4. Call Invites
        const unsubscribeCall = socketService.on(SocketEvents.CALL_INVITATION, (data: any) => {
            toast(`Incoming ${data.callType === 'audio' ? 'Audio' : 'Video'} Call`, {
                description: `${data.callerName} is calling you`,
                action: {
                    label: 'Join',
                    onClick: () => navigate(`/room/${data.roomId}`)
                },
                duration: 30000, // 30 seconds
            });
        });

        // 5. Follow-up Reminders
        const unsubscribeReminder = socketService.on(SocketEvents.EMAIL_REMINDER, (data: any) => {
            toast('Follow-up Reminder', {
                description: data.message || `Reminder for: ${data.subject}`,
                action: {
                    label: 'Open',
                    onClick: () => navigate(`/email/${data.emailId}`)
                }
            });
        });

        // 6. Unsnoozed Emails
        const unsubscribeUnsnoozed = socketService.on(SocketEvents.EMAIL_UNSNOOZED, (data: any) => {
            toast.info('Email Unsnoozed', {
                description: data.subject || 'An email has returned to your inbox',
                action: {
                    label: 'Open',
                    onClick: () => navigate(`/email/${data.emailId}`)
                }
            });
        });

        return () => {
            unsubscribeNotification();
            unsubscribeEmail();
            unsubscribeMessage();
            unsubscribeCall();
            unsubscribeReminder();
            unsubscribeUnsnoozed();
        };
    }, [user, navigate]);

    return <>{children}</>;
};
